import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Copy, Check, ArrowRight, Phone, Mail, Printer, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { TopoBackground } from "@/components/TopoBackground";
import { getRequest } from "@/lib/api";

const Row = ({ label, value }) => (
  <div className="flex flex-col gap-0.5 border-b border-[#e6eae7] py-2.5 last:border-0 sm:flex-row sm:justify-between">
    <span className="text-sm text-[#5a6660]">{label}</span>
    <span className="text-sm font-medium text-[#1a221e] sm:text-right">{value || "—"}</span>
  </div>
);

export default function Confirmation() {
  const { ref } = useParams();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [copied, setCopied] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    getRequest(ref).then(setReq).catch(() => setNotFound(true));
  }, [ref]);

  const copy = () => {
    navigator.clipboard.writeText(ref);
    setCopied(true);
    toast.success("Reference number copied");
    setTimeout(() => setCopied(false), 2000);
  };

  if (notFound) {
    return (
      <>
        <Header />
        <div className="mx-auto max-w-xl px-4 py-24 text-center">
          <h1 className="font-display text-2xl font-bold text-[#163323]">Request not found</h1>
          <p className="mt-2 text-[#5a6660]">We couldn't find a request for {ref}.</p>
          <Button onClick={() => navigate("/")} className="mt-6 rounded-full bg-[#163323] text-white hover:bg-[#0d2116]">
            <Home className="mr-2 h-4 w-4" /> Back home
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="relative overflow-hidden bg-gradient-to-br from-[#163323] via-[#11291c] to-[#0d2116] py-16 text-white">
        <TopoBackground opacity={0.12} />
        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#c8a452]/15 ring-2 ring-[#c8a452]/40">
            <CheckCircle2 className="h-9 w-9 text-[#c8a452]" />
          </div>
          <h1 className="mt-6 font-display text-3xl font-bold sm:text-4xl">Request submitted</h1>
          <p className="mx-auto mt-3 max-w-lg text-white/70">
            Thank you. Your Construction Surveying request has been received. Our team will review it
            and reach out via your preferred contact method.
          </p>
          <div className="mx-auto mt-8 inline-flex flex-col items-center gap-2 rounded-2xl border border-[#c8a452]/30 bg-black/20 px-8 py-5 backdrop-blur">
            <span className="text-xs uppercase tracking-[0.2em] text-[#c8a452]">Your reference number</span>
            <div className="flex items-center gap-3">
              <span data-testid="confirmation-ref" className="font-mono text-3xl font-bold sm:text-4xl">{ref}</span>
              <button data-testid="copy-ref-btn" onClick={copy}
                className="rounded-lg bg-[#c8a452]/15 p-2 text-[#c8a452] transition-colors hover:bg-[#c8a452]/25">
                {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <div className="rounded-2xl border border-[#e6eae7] bg-white p-6 shadow-sm sm:p-8">
          <h2 className="font-display text-lg font-semibold text-[#163323]">What happens next</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-3">
            {[
              ["1", "Review", "We review your details and documents."],
              ["2", "Quotation", "We prepare and send a tailored quote."],
              ["3", "Schedule", "Once approved, we schedule your survey."],
            ].map(([n, t, d]) => (
              <div key={n} className="rounded-xl border border-[#e6eae7] bg-[#f8f9f8] p-4">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#163323] font-mono text-sm font-bold text-[#c8a452]">{n}</span>
                <h3 className="mt-3 text-sm font-semibold text-[#163323]">{t}</h3>
                <p className="mt-1 text-xs leading-relaxed text-[#5a6660]">{d}</p>
              </div>
            ))}
          </div>

          {req && (
            <div className="mt-8">
              <h2 className="font-display text-lg font-semibold text-[#163323]">Request summary</h2>
              <div className="mt-3">
                <Row label="Reference" value={req.reference_number} />
                <Row label="Service" value={req.service_type} />
                <Row label="Name" value={req.customer?.full_name} />
                <Row label="Company" value={req.customer?.company_name} />
                <Row label="Email" value={req.customer?.email} />
                <Row label="Phone" value={req.customer?.phone} />
                <Row label="Location" value={req.location?.address} />
                <Row label="Island" value={req.location?.island} />
                <Row label="Project type" value={req.construction?.project_type} />
                <Row label="Work required" value={(req.construction?.work_required || []).join(", ")} />
                <Row label="Preferred date" value={req.construction?.preferred_service_date} />
                <Row label="Documents" value={`${(req.documents || []).length} file(s)`} />
              </div>
            </div>
          )}

          <div className="mt-8 rounded-xl bg-[#fff8e6] p-4 text-sm text-[#8a6d15]">
            A confirmation email has been sent to you. Submitting this request does not confirm a booking.
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button data-testid="print-btn" onClick={() => window.print()} variant="outline"
              className="h-11 rounded-full border-[#d2dad5] text-[#163323] hover:bg-[#f2f4f2]">
              <Printer className="mr-2 h-4 w-4" /> Print summary
            </Button>
            <a href="tel:+12428226000">
              <Button variant="outline" className="h-11 w-full rounded-full border-[#d2dad5] text-[#163323] hover:bg-[#f2f4f2] sm:w-auto">
                <Phone className="mr-2 h-4 w-4" /> Call us
              </Button>
            </a>
            <a href="mailto:dataearthsurveying@gmail.com">
              <Button variant="outline" className="h-11 w-full rounded-full border-[#d2dad5] text-[#163323] hover:bg-[#f2f4f2] sm:w-auto">
                <Mail className="mr-2 h-4 w-4" /> Email us
              </Button>
            </a>
            <Link to="/" className="sm:ml-auto">
              <Button className="h-11 w-full rounded-full bg-[#163323] font-semibold text-white hover:bg-[#0d2116] sm:w-auto">
                Back home <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
